// src/admin/AdminConversionSummary.js
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import api from "../utils/api";
import { API } from "../config/apiConfig";
import AdminPageWrapper from './components/AdminPageWrapper';

// Fallback: prefer API.adminConversionsSummary; else `${API.adminConversions}/summary`; else default path
const SUMMARY_ENDPOINT =
  API.adminConversionsSummary ||
  (API.adminConversions ? `${API.adminConversions}/summary` : "/api/admin/conversions/summary");

function formatDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function firstOfThisMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}
function lastOfThisMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
}

function fmtNum(val, decimals = 2) {
  const num = typeof val === "number" ? val : Number(val ?? 0);
  if (!isFinite(num)) return "0";
  return num.toFixed(decimals);
}
function fmtCurrency(val) {
  const num = typeof val === "number" ? val : Number(val ?? 0);
  if (!isFinite(num)) return "$0.00";
  return `$${num.toFixed(2)}`;
}

export default function AdminConversionSummary() {
  const [summary, setSummary] = useState([]); // [{ AppName, TotalProviderRevenue, CompanyRevenue, DistributableAmount, TotalTokens, ConversionRate, TotalUserDistributed }]
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [start, setStart] = useState(firstOfThisMonth());
  const [end, setEnd] = useState(lastOfThisMonth());

  const headerText = useMemo(
    () => `${formatDate(start)} → ${formatDate(end)}`,
    [start, end]
  );

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const params = {
        periodStart: formatDate(start),
        periodEnd: formatDate(end),
      };
      const { data } = await api.get(SUMMARY_ENDPOINT, { params });
      setSummary(Array.isArray(data?.summary) ? data.summary : []);
      setSuccessMsg("Summary refreshed");
    } catch (err) {
      console.error("❌ Failed to fetch conversion summary:", err);
      setErrorMsg(err?.response?.data?.error || "Failed to fetch conversion summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleThisMonth = async () => {
    const s = firstOfThisMonth();
    const e = lastOfThisMonth();
    setStart(s);
    setEnd(e);
    await fetchSummary();
  };

  // Simple guard: ensure end >= start
  const handleStartChange = (e) => {
    const d = new Date(e.target.value);
    if (isNaN(d)) return;
    setStart(d);
    if (end < d) setEnd(d);
  };
  const handleEndChange = (e) => {
    const d = new Date(e.target.value);
    if (isNaN(d)) return;
    if (d < start) return; // ignore invalid backward range
    setEnd(d);
  };

  return (
    <AdminPageWrapper 
      title="Conversion Summary" 
      subtitle="View token conversion statistics and analytics"
    >

      {/* Filters */}
      <Box display="flex" gap={2} alignItems="center" mb={1}>
        <TextField
          label="Start"
          type="date"
          value={formatDate(start)}
          onChange={handleStartChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="End"
          type="date"
          value={formatDate(end)}
          onChange={handleEndChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />

        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchSummary} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Button
          variant="outlined"
          startIcon={<CalendarMonthIcon />}
          onClick={handleThisMonth}
        >
          This Month
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Period: {headerText}
      </Typography>

      {/* Content */}
      {loading ? (
        <Box textAlign="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : summary.length === 0 ? (
        <Box textAlign="center" mt={4}>
          <Typography>No data for selected period</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {summary.map((row, idx) => {
            const appName = row.AppName ?? "Unknown App";
            const providerRev = fmtCurrency(row.TotalProviderRevenue);
            const companyRev = fmtCurrency(row.CompanyRevenue);
            const distributable = fmtCurrency(row.DistributableAmount);
            const tokens = fmtNum(row.TotalTokens, 0);
            const rate = `$${fmtNum(row.ConversionRate, 6)}/token`;
            const userPaid = fmtCurrency(row.TotalUserDistributed);

            return (
              <Grid item xs={12} key={`${appName}-${idx}`}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {appName}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Metric label="Provider Revenue" value={providerRev} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Metric
                          label="Company Revenue (15%)"
                          value={companyRev}
                          color="orange"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Metric
                          label="Distributable (85%)"
                          value={distributable}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Metric label="Total Tokens" value={tokens} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Metric label="Conversion Rate" value={rate} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Metric label="User Distributed" value={userPaid} />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Snackbar */}
      <Snackbar
        open={!!errorMsg || !!successMsg}
        autoHideDuration={3500}
        onClose={() => {
          setErrorMsg("");
          setSuccessMsg("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={errorMsg ? "error" : "success"}
          onClose={() => {
            setErrorMsg("");
            setSuccessMsg("");
          }}
          sx={{ width: "100%" }}
        >
          {errorMsg || successMsg}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
}

function Metric({ label, value, color, weight = 600 }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box mt={0.5}>
        <Typography
          variant="body1"
          sx={{ fontWeight: weight, color: color ? color : "inherit" }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}
